import React from 'react';

import dayjs from 'utils/extendedDayJS';
import {
  DataViewLine,
  DataViewLineIcon,
  DataViewLineContent,
  DataViewLineHeading,
  DataViewLineSubText
} from '@epoch-ui/components/DataViewLine/DataViewLine';
import { Icon } from '@epoch-ui/components/Icon';
import { GuestActivity, GuestActivityType } from 'types/rsvp_control.types';

interface GuestActivityCardProps {
  activity: GuestActivity;
  timezone?: string;
}

const getActivityIcon = (activity: GuestActivity): JSX.Element => {
  switch (activity.type) {
    case GuestActivityType.INVITED:
      return <Icon size={16} name="user-plus" className="tw-text-black" />;
    case GuestActivityType.UNINVITED:
      return <Icon size={16} name="user-x" className="tw-text-black" />;
    case GuestActivityType.RSVP_AS_GOING:
      return (
        <Icon size={16} name="circle-check" className="tw-text-background-grey" fill="#059450" />
      );
    case GuestActivityType.MARKED_AS_NOT_GOING:
      return <Icon size={16} name="circle-x" className="tw-text-negative-foreground" />;
    case GuestActivityType.RSVP_CHANGED_TO_PENDING:
      return <Icon size={16} name="clock" className="tw-text-content-tertiary" />;
    case GuestActivityType.MARKED_AS_INCOMPLETE:
      return <Icon size={16} name="clock" className="tw-text-warning-foreground" />;
    case GuestActivityType.RSVP_AS_MAYBE:
      return <Icon size={16} name="bookmark" className="tw-text-rsvp-blue" />;
    case GuestActivityType.RSVP_AS_WAITLISTED:
      return <Icon size={16} name="hourglass" className="tw-text-accent-purple" />;
    default:
      return <Icon size={16} name="clock" className="tw-text-content-tertiary" />;
  }
};

export const GuestActivityCard: React.FC<GuestActivityCardProps> = ({ activity, timezone }) => {
  const icon = getActivityIcon(activity);

  const formattedTimestamp = dayjs.formatPrettyDateTime({
    dateTime: activity.datetime,
    timezoneCode: timezone,
    customFormat: 'MMM D, YYYY, h:mm A'
  });

  const subtext = activity.attributionString
    ? `${formattedTimestamp} ${activity.attributionString}`
    : formattedTimestamp;

  return (
    <DataViewLine className="tw-gap-3">
      <DataViewLineIcon className="tw-rounded-full tw-bg-background-grey">{icon}</DataViewLineIcon>
      <DataViewLineContent className="tw-gap-1">
        <DataViewLineHeading className="tw-font-medium tw-leading-4">
          {activity.title}
        </DataViewLineHeading>
        <DataViewLineSubText className="tw-text-xs tw-font-normal">{subtext}</DataViewLineSubText>
      </DataViewLineContent>
    </DataViewLine>
  );
};
